import { useContext } from "react"
import * as PropTypes from "prop-types"
import styled from "styled-components"
import { useT } from "react-polyglot-hooks"
import Typography from "components/styled/Typography"
import { Container } from "components/styled/Grid"
import { printNumber } from "helpers/formatter"
import { scrollToRef } from "helpers/scroll"
import { sendEvent } from "scripts/GoogleAnalytics"
import Icon from "components/icons/icon"
import { Check } from "components/icons/check"
import { PrimaryButton } from "components/styled/Buttons/PrimaryButton"
import { color, mq } from "helpers/styled"
import { CarContext } from "../../CarContext"

const Background = styled.div`
  cursor: pointer;
  position: relative;
  padding: 10px 25px;

  @media ${mq("md")} {
    padding-left: 50px;
  }

  @media ${mq("lg")} {
    padding: 10px 20px;
  }

  &:first-child {
    border-top: solid 1px ${color("silver")};
  }

  &:nth-child(-n + 5) {
    border-bottom: solid 1px ${color("silver")};
  }

  &:last-child {
    margin-bottom: -25px;
  }

  &.selected {
    background: ${color("paleGray")};

    @media ${mq("md")} {
      box-shadow: 0 4px 10px 0 rgba(2, 24, 89, 0.3);
    }
  }

  &:nth-child(n + 6) {
    height: 0;
    opacity: 0;
    overflow: hidden;
    padding-top: 0;
    padding-bottom: 0;
    transition:
      height 0.5s,
      opacity 0.5s,
      padding-top 0.5s,
      padding-bottom 0.5s;

    .show-all & {
      height: 61px;
      opacity: 1;
      padding-top: 10px;
      padding-bottom: 10px;
      border-bottom: solid 1px ${color("silver")};
    }
  }
`

const Wrapper = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 0 5px;

  @media ${mq("md")} {
    padding: 0 25px;
  }

  @media ${mq("lg")} {
    width: 100%;
    padding: 0;
  }
`

const Selected = styled.div`
  background: ${({ theme }) => theme.colors.skyBlue};
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 25px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${mq("md")} {
    width: 50px;
  }
`

const StyledIcon = styled(Icon)`
  font-size: 8px;
  width: 8px;
  height: 8px;
  @media ${mq("sm")} {
    font-size: 15px;
    width: 15px;
    height: 15px;
  }
  @media ${mq("lg")} {
    font-size: 24px;
    width: 24px;
    height: 24px;
  }
`

const Italic = styled(Typography)`
  font-style: italic;
`

const Text = styled(Typography)`
  display: block;
  &:first-child {
    white-space: nowrap;
    margin-right: 15px;
    margin-bottom: 5px;
  }
  @media ${mq("md")} {
    display: inline-block;
    &:first-child {
      margin-right: ${({ small }) => (small ? "0" : "50px")};
      margin-bottom: 0;
    }
  }

  @media ${mq("lg")} {
    display: block;
  }
`

const Button = styled(PrimaryButton)`
  margin: 0;

  @media ${mq("md")} {
    width: 150px;
    font-size: 16px;
    padding-top: 9px;
    padding-bottom: 9px;
  }
  @media ${mq("lg")} {
    width: 90px;
    font-size: 12px;
    padding-top: 4px;
    padding-bottom: 4px;
  }
`

export default function Plan({ plan, small, withoutTaxes }) {
  const { id, kmYear: km, duration, price, priceWithoutTaxes } = plan
  const { selectedPlan, setSelectedPlan, formRef, page, car } = useContext(CarContext)
  const t = useT()
  const { sellingStatus} = car

  const handleClick = () => {
    setSelectedPlan(plan)
    setTimeout(() => scrollToRef(formRef), 200)
    sendEvent("Click CTA plans", { event_category: page, event_label: "Plan selected" })
  }

  return (
    <Background className={!small && selectedPlan.id === id ? "selected" : null} onClick={handleClick}>
      {!small && selectedPlan.id === id && (
        <Selected>
          <StyledIcon color="white" Icon={Check} />
        </Selected>
      )}
      <Wrapper>
        <div>
          <Text variant="small" weight="medium" small={small}>
            {t("car_data.plans.km", { km: printNumber(km) })}
          </Text>
          <Text variant="small" weight="medium" small={small}>
            {t("car_data.plans.month", { duration })}
          </Text>
        </div>
        <div>
          <Typography variant={small ? "paragraph" : "title3"} color="skyBlue" weight="bold" className="m-0">
            {`${withoutTaxes ? priceWithoutTaxes : price} €`}
          </Typography>
          <Italic variant="small">{`/${t("common.month")}`}
            {sellingStatus !== 'flexible' ? ` · ${t(
            withoutTaxes ? "common.without_tax" : "car_data.plans.all_included"
          )}` : ''}</Italic>
        </div>
        <Button size="xs">{t("car_data.plans.cta")}</Button>
      </Wrapper>
    </Background>
  )
}

Plan.defaultProps = {
  small: false
}

Plan.propTypes = {
  plan: PropTypes.object.isRequired,
  small: PropTypes.bool,
  withoutTaxes: PropTypes.bool.isRequired
}
